import { createTRPCContext } from '@trpc/tanstack-react-query';
import type { AppRouter } from 'config-hub-server/src/router/index.js';

/**
 * The typed client. `AppRouter` is a TYPE import — nothing from the server is bundled.
 *
 * @trpc/server must resolve to exactly one copy across both workspaces (pinned by the root
 * `overrides`), or the two halves end up with different TRPC_ERROR_CODE unions and this
 * import stops typechecking.
 */
export const { TRPCProvider, useTRPC, useTRPCClient } = createTRPCContext<AppRouter>();

/**
 * `meta: { quiet: true }` on a query opts it out of the global red toast in `App.tsx`.
 *
 * For the one case where the page itself explains the failure better than a toast can — the
 * Studio page's "No studio `<key>` in master", which otherwise says the same thing twice,
 * once inline and once as a toast that has already faded by the time anyone reads it. Every
 * other query keeps the global handler; this is an opt-out, not a pattern to spread.
 *
 * On a mutation, `meta: { quiet: true }` is for one that reports its own failure in place (the
 * editor's save banner, the push review, a dialog) or is summarised by its caller (Pull all's
 * per-environment calls).
 *
 * `meta: { skipInvalidate: true }` on a mutation opts it out of the targeted invalidation in
 * `App.tsx`'s `MutationCache.onSuccess` (`utils/invalidation.ts`'s `shouldInvalidate`) — for a
 * mutation run several times in a loop under one caller-owned wrapper (Pull all's per-environment
 * `pullNow`), where invalidating after every iteration is wasted work the wrapper's own success
 * already covers once.
 */
declare module '@tanstack/react-query' {
    interface Register {
        queryMeta: { quiet?: boolean };
        mutationMeta: { quiet?: boolean; skipInvalidate?: boolean };
    }
}
